<template>
  <s-tooltip :text="t('tooltip')" wrapperClass="flex-1 flex">
    <template #default="{tooltipId}">
      <Popover class="relative flex-1 flex" v-slot="{open}">
        <PopoverButton
          ref="buttonRef"
          class="flex-1 group/button flex items-center justify-center px-3 py-1.5 text-blue-400 hover:text-blue-600 hover:bg-gray-100 transition focus-visible:outline-blue-400 -outline-offset-2"
          :aria-labelledby="tooltipId"
          @click="onOpen"
        >
          <s-icon name="book-open-page-variant" size="20" aria-hidden="true" />
          <span class="sr-only">{{ t('tooltip') }}</span>
        </PopoverButton>

        <Teleport to="body">
          <transition
            enter-active-class="transition-opacity duration-100 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity duration-75 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <PopoverPanel
              v-if="open"
              ref="panelRef"
              focus
              class="z-[60] w-[32rem] max-w-[calc(100vw-1rem)] rounded-md shadow-lg ring-1 ring-black/10 bg-white focus:outline-none"
              :style="floatingStyles"
            >
              <div class="px-3 py-2 border-b border-gray-250 flex items-center gap-2">
                <s-icon
                  name="book-open-page-variant"
                  size="20"
                  aria-hidden="true"
                  class="text-gray-600"
                />
                <h3 class="text-sm font-medium text-gray-900 mt-px">{{ t('title') }}</h3>
              </div>

              <div class="max-h-64 overflow-y-auto scroll-shadow scroll-shadow--gray-50 bg-gray-50">
                <div
                  v-if="loading"
                  class="p-4 text-center text-gray-500 flex items-center justify-center gap-2"
                >
                  <s-loading-spinner size="inline" />
                  {{ t('loading') }}
                </div>

                <div v-else-if="error" class="p-4 text-center text-red-500">
                  {{ t('error') }}
                </div>

                <div
                  v-else-if="articles.length === 0"
                  class="bg-gray-50 p-4 text-center italic text-gray-500"
                >
                  {{ t('noArticles') }}
                </div>

                <ul v-else class="divide-y divide-gray-200">
                  <li v-for="article in articles" :key="article.id">
                    <a
                      :href="article.url"
                      :title="stripHtml(article.title)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="group flex items-start gap-2 p-3 hover:bg-white transition"
                    >
                      <s-icon
                        name="file-document-outline"
                        size="22"
                        class="text-gray-400 group-hover:text-blue-500 transition ease-out shrink-0 mt-0.5"
                      />
                      <div class="min-w-0 flex-1">
                        <p
                          class="underline underline-offset-2 decoration-transparent focus:decoration-blue-200/50 group-hover:decoration-blue-200/50 active:decoration-blue-300 decoration-2 font-medium text-blue-600 group-hover:text-blue-500 focus:text-blue-500 active:text-blue-700 tracking-slight transition-all ease-out duration-100 truncate mb-0.5"
                        >
                          {{ stripHtml(article.title) }}
                        </p>
                        <p v-if="article.description" class="text-sm text-gray-500 line-clamp-3">
                          {{ stripHtml(article.description) }}
                        </p>
                      </div>
                      <s-icon
                        name="open-in-new"
                        size="16"
                        class="text-gray-400 group-hover:text-blue-500 transition ease-out shrink-0 mt-1"
                      />
                    </a>
                  </li>
                </ul>
              </div>

              <div class="border-t border-gray-250 bg-white rounded-b-md">
                <a
                  href="https://support.stemble.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="group flex items-center justify-center gap-1 text-sm link-subtle py-2"
                >
                  {{ t('viewAll') }}
                  <s-icon
                    name="open-in-new"
                    size="1.125em"
                    class="text-gray-400 group-hover:text-blue-500 transition ease-out"
                  />
                </a>
              </div>
            </PopoverPanel>
          </transition>
        </Teleport>
      </Popover>
    </template>
  </s-tooltip>
</template>

<script setup lang="ts">
import {ref, watch} from 'vue';
import {Popover, PopoverButton, PopoverPanel} from '@headlessui/vue';
import {autoUpdate, flip, offset, shift, useFloating} from '@floating-ui/vue';
import {useI18n} from 'vue-i18n';
import {usePage} from '@inertiajs/vue3';
import {route} from 'ziggy-js';
import STooltip from '../design-system/STooltip.vue';
import SIcon from '../design-system/SIcon.vue';
import SLoadingSpinner from '../design-system/SLoadingSpinner.vue';

interface HelpArticle {
  id: string;
  title: string;
  url: string;
  description?: string | null;
}

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

const buttonRef = ref<HTMLElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);

const loading = ref(false);
const error = ref(false);
const articles = ref<HelpArticle[]>([]);
const hasFetched = ref(false);

const {floatingStyles} = useFloating(buttonRef, panelRef, {
  placement: 'bottom-end',
  middleware: [offset(8), shift({padding: 8}), flip({padding: 16, fallbackStrategy: 'bestFit'})],
  whileElementsMounted: autoUpdate,
});

async function fetchArticles() {
  if (hasFetched.value) return;

  const currentRoute = route().current();
  if (!currentRoute) return;

  loading.value = true;
  error.value = false;

  try {
    const url = route('api.v1.help-articles.index', {
      route: currentRoute,
      url_path: window.location.pathname,
      route_params: route().params,
    });

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error('Failed to fetch articles');
    }

    const data = await response.json();
    articles.value = data.data ?? [];
    hasFetched.value = true;
  } catch (e) {
    console.error('Failed to fetch help articles:', e);
    error.value = true;
  } finally {
    loading.value = false;
  }
}

function onOpen() {
  fetchArticles();
}

const page = usePage();

function stripHtml(text: string | null | undefined): string {
  if (!text) return '';
  return text.replace(/<[^>]*>/g, '');
}

// Reset fetch state when route changes
watch(
  () => page.url,
  () => {
    hasFetched.value = false;
    articles.value = [];
  }
);
</script>

<i18n>
{
  "en": {
    "tooltip": "Help Articles",
    "title": "Help Articles",
    "loading": "Loading articles...",
    "error": "Failed to load articles",
    "noArticles": "No articles found for this page",
    "viewAll": "View all help articles"
  },
  "fr": {
    "tooltip": "Articles d'aide",
    "title": "Articles d'aide",
    "loading": "Chargement des articles...",
    "error": "Impossible de charger les articles",
    "noArticles": "Aucun article trouvé pour cette page",
    "viewAll": "Voir tous les articles d'aide"
  }
}
</i18n>
